import * as React from 'react'
import { Link } from 'react-router-dom'
import { BookOpen, CalendarClock, ChevronDown, ChevronUp, ExternalLink, GraduationCap, MapPin, Pencil, Plus, Trash2, User, X } from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { useQuery } from '@/hooks/use-query'
import { useDirty, useDiscardGuard } from '@/hooks/use-unsaved'
import { formatDate, formatDateTime } from '@/lib/format'
import type { Activity, Profile, Workshop, WorkshopAttendee, WorkshopInternalNote, WorkshopStatus, WorkshopTopic } from '@/lib/types'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogDescription,
  DialogFullScreenContent,
  DialogTitle,
} from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { EmptyState, ErrorNote, Loading, Markdown, Page } from '@/components/page'
import { toLocalInput } from '@/pages/activity-editor'
import { DiscardPrompt } from '@/components/discard-prompt'

const RSVPS = [
  { value: 'yes', label: 'Going' },
  { value: 'maybe', label: 'Maybe' },
  { value: 'no', label: 'Not going' },
] as const

export function WorkshopsPage() {
  const { currentTeam, session, isLead, isCoach } = useAuth()
  const teamId = currentTeam?.id
  const userId = session?.user?.id
  const [editing, setEditing] = React.useState<Workshop | 'new' | null>(null)
  const [expanded, setExpanded] = React.useState<string | null>(null)

  const { data, loading, error, refetch } = useQuery(async () => {
    if (!teamId) {
      return {
        workshops: [] as Workshop[],
        attendees: [] as WorkshopAttendee[],
        profiles: [] as Profile[],
        topics: [] as WorkshopTopic[],
        activities: [] as Activity[],
        internal: [] as WorkshopInternalNote[],
      }
    }
    const [ws, at, pr, wt, ac, iN] = await Promise.all([
      sb().from('workshops').select('*').eq('team_id', teamId).order('starts_at', { ascending: false, nullsFirst: false }),
      sb().from('workshop_attendees').select('*').eq('team_id', teamId),
      sb().from('profiles').select('*'),
      sb().from('workshop_topics').select('*').eq('team_id', teamId).order('display_order'),
      // The pool the picker offers, and the source of the LIVE title for a
      // lesson whose topic still exists. Hidden topics (0019) never come back
      // from this read, which is what keeps them out of the picker without the
      // page having to filter.
      sb()
        .from('activities')
        .select('id, team_id, title, kind, display_order')
        .eq('team_id', teamId)
        .order('display_order'),
      // DPL's own notes. No client-side gate is needed or wanted: the policy in
      // 0035 returns nothing at all to anyone who isn't a coach, so a member's
      // browser never holds a row it shouldn't show.
      sb().from('workshop_internal_notes').select('*').eq('team_id', teamId),
    ])
    if (ws.error) throw ws.error
    if (at.error) throw at.error
    if (pr.error) throw pr.error
    if (wt.error) throw wt.error
    if (ac.error) throw ac.error
    if (iN.error) throw iN.error
    return {
      workshops: (ws.data ?? []) as Workshop[],
      attendees: (at.data ?? []) as WorkshopAttendee[],
      profiles: (pr.data ?? []) as Profile[],
      topics: (wt.data ?? []) as WorkshopTopic[],
      activities: (ac.data ?? []) as Activity[],
      internal: (iN.data ?? []) as WorkshopInternalNote[],
    }
  }, [teamId])

  const now = Date.now()
  const { upcoming, past } = React.useMemo(() => {
    const up: Workshop[] = []
    const pa: Workshop[] = []
    for (const w of data?.workshops ?? []) {
      const isPast =
        w.status === 'complete' ||
        w.status === 'cancelled' ||
        (w.starts_at ? new Date(w.starts_at).getTime() < now : false)
      ;(isPast ? pa : up).push(w)
    }
    // Upcoming reads soonest-first; the query returns newest-first, which is
    // right for history but backwards for a schedule.
    up.reverse()
    return { upcoming: up, past: pa }
  }, [data?.workshops, now])

  /** DPL's note for one workshop, or undefined. Empty for anyone but a coach. */
  function internalFor(workshopId: string): WorkshopInternalNote | undefined {
    return (data?.internal ?? []).find((n) => n.workshop_id === workshopId)
  }

  async function setRsvp(w: Workshop, rsvp: 'yes' | 'no' | 'maybe') {
    if (!userId) return
    const { error: err } = await sb()
      .from('workshop_attendees')
      .upsert({ workshop_id: w.id, team_id: w.team_id, user_id: userId, rsvp }, { onConflict: 'workshop_id,user_id' })
    if (err) window.alert(err.message)
    void refetch()
  }

  async function remove(w: Workshop) {
    if (!window.confirm(`Delete “${w.title}”?`)) return
    const { error: err } = await sb().from('workshops').delete().eq('id', w.id)
    if (err) return window.alert(err.message)
    void refetch()
  }

  const renderList = (list: Workshop[]) =>
    list.map((w) => {
      const mine = data?.attendees.find((a) => a.workshop_id === w.id && a.user_id === userId)
      const going = (data?.attendees ?? []).filter((a) => a.workshop_id === w.id && a.rsvp === 'yes')
      const lessonCount = (data?.topics ?? []).filter((t) => t.workshop_id === w.id).length
      const open = expanded === w.id
      return (
        <Card key={w.id}>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    className="text-left font-medium hover:underline"
                    onClick={() => setExpanded(open ? null : w.id)}
                  >
                    {w.title}
                  </button>
                  <StatusBadge status={w.status} />
                </div>
                {w.description && !open && (
                  <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{w.description}</p>
                )}
                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  {w.starts_at && (
                    <span className="inline-flex items-center gap-1">
                      <CalendarClock className="h-3 w-3" /> {formatDateTime(w.starts_at)}
                      {w.ends_at && ` – ${new Date(w.ends_at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`}
                    </span>
                  )}
                  {w.location && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {w.location}
                    </span>
                  )}
                  {w.facilitator && (
                    <span className="inline-flex items-center gap-1">
                      <User className="h-3 w-3" /> {w.facilitator}
                    </span>
                  )}
                  <span>
                    {going.length} going
                  </span>
                  {lessonCount > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <BookOpen className="h-3 w-3" />
                      {lessonCount} {lessonCount === 1 ? 'lesson' : 'lessons'}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-1.5">
                {w.status === 'scheduled' && (
                  <Select value={mine?.rsvp ?? ''} onValueChange={(v) => void setRsvp(w, v as 'yes')}>
                    <SelectTrigger className="h-8 w-[116px] text-xs">
                      <SelectValue placeholder="RSVP" />
                    </SelectTrigger>
                    <SelectContent>
                      {RSVPS.map((r) => (
                        <SelectItem key={r.value} value={r.value}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {w.meeting_url && (
                  <Button variant="outline" size="sm" asChild>
                    <a href={w.meeting_url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink /> Join
                    </a>
                  </Button>
                )}
                <Button variant="ghost" size="icon-sm" onClick={() => setEditing(w)} aria-label="Edit workshop">
                  <Pencil />
                </Button>
                {isLead && (
                  <Button variant="ghost" size="icon-sm" onClick={() => void remove(w)} aria-label="Delete workshop">
                    <Trash2 />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setExpanded(open ? null : w.id)}
                  aria-label={open ? 'Collapse' : 'Expand'}
                >
                  <ChevronDown className={cn('transition-transform', open && 'rotate-180')} />
                </Button>
              </div>
            </div>

            {open && (
              <div className="mt-4 space-y-4 border-t pt-4">
                {w.description && <p className="text-sm text-muted-foreground">{w.description}</p>}
                {/* Lessons before the agenda: the agenda is prose about how the
                    session runs, and it reads as commentary on a running order
                    the reader has already seen. */}
                <WorkshopLessons
                  lessons={(data?.topics ?? []).filter((t) => t.workshop_id === w.id)}
                  activities={data?.activities ?? []}
                />
                {w.agenda && (
                  <div>
                    <h3 className="mb-1.5 text-sm font-semibold">Agenda</h3>
                    <Markdown source={w.agenda} />
                  </div>
                )}
                {w.notes && (
                  <div>
                    <h3 className="mb-1.5 text-sm font-semibold">Meeting notes</h3>
                    <Markdown source={w.notes} />
                  </div>
                )}
                {w.outcomes && (
                  <div>
                    <h3 className="mb-1.5 text-sm font-semibold">Outcomes</h3>
                    <Markdown source={w.outcomes} />
                  </div>
                )}
                {/* Only a coach ever HAS one of these rows — the policy in 0035
                    returns nothing to anyone else — so this renders whatever
                    came back rather than checking a role again. The badge is
                    for the coach's own benefit: it is the reminder that the
                    person sitting next to them cannot see this block. */}
                {internalFor(w.id)?.notes && (
                  <div className="rounded-md border border-dashed border-warning/50 bg-warning/5 p-3">
                    <h3 className="mb-1.5 flex items-center gap-2 text-sm font-semibold">
                      Internal notes
                      <Badge variant="outline" className="border-warning/50 text-[10px] font-medium uppercase tracking-wide text-warning">
                        DPL only
                      </Badge>
                    </h3>
                    <Markdown source={internalFor(w.id)!.notes!} />
                  </div>
                )}
                <div>
                  <h3 className="mb-1.5 text-sm font-semibold">Attendees</h3>
                  {(data?.attendees ?? []).filter((a) => a.workshop_id === w.id).length === 0 ? (
                    <p className="text-sm text-muted-foreground">No responses yet.</p>
                  ) : (
                    <ul className="space-y-1 text-sm">
                      {(data?.attendees ?? [])
                        .filter((a) => a.workshop_id === w.id)
                        .map((a) => {
                          const p = data?.profiles.find((x) => x.user_id === a.user_id)
                          return (
                            <li key={a.id} className="flex items-center justify-between gap-2">
                              <span className="truncate">{p?.display_name || p?.email || 'Someone'}</span>
                              <Badge variant={a.rsvp === 'yes' ? 'success' : a.rsvp === 'no' ? 'muted' : 'warning'} className="text-[10px]">
                                {RSVPS.find((r) => r.value === a.rsvp)?.label}
                              </Badge>
                            </li>
                          )
                        })}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )
    })

  return (
    <Page
      title="Workshops"
      description="Working sessions — scheduled, and what came out of them."
      actions={
        <Button onClick={() => setEditing('new')}>
          <Plus /> New workshop
        </Button>
      }
    >
      {error && <ErrorNote error={error} />}
      {loading && !data ? (
        <Loading />
      ) : (data?.workshops.length ?? 0) === 0 ? (
        <EmptyState
          icon={GraduationCap}
          title="No workshops yet"
          description="Schedule a working session and capture its agenda and outcomes here."
          action={
            <Button onClick={() => setEditing('new')}>
              <Plus /> Schedule the first one
            </Button>
          }
        />
      ) : (
        <div className="space-y-6">
          {upcoming.length > 0 && (
            <section>
              <h2 className="mb-2.5 text-sm font-semibold text-muted-foreground">Upcoming</h2>
              <div className="space-y-2.5">{renderList(upcoming)}</div>
            </section>
          )}
          {past.length > 0 && (
            <section>
              <h2 className="mb-2.5 text-sm font-semibold text-muted-foreground">Past</h2>
              <div className="space-y-2.5">{renderList(past)}</div>
            </section>
          )}
        </div>
      )}

      {editing && teamId && (
        <WorkshopEditor
          teamId={teamId}
          workshop={editing === 'new' ? undefined : editing}
          activities={data?.activities ?? []}
          lessons={
            editing === 'new'
              ? []
              : (data?.topics ?? []).filter((t) => t.workshop_id === editing.id)
          }
          isCoach={isCoach}
          internalNote={editing === 'new' ? undefined : internalFor(editing.id)}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            void refetch()
          }}
        />
      )}
    </Page>
  )
}

/**
 * The workshop's running order, on the expanded card.
 *
 * The live topic title wins while the link holds, so renaming a topic renames
 * it here; the stored `title` is only the fallback for a lesson whose topic was
 * deleted after the fact. That row is deliberately kept (0033) — a workshop
 * that already happened is a record of what was taught — and is flagged rather
 * than rendered as a dead link.
 */
function WorkshopLessons({
  lessons,
  activities,
}: {
  lessons: WorkshopTopic[]
  activities: Activity[]
}) {
  if (lessons.length === 0) return null
  return (
    <div>
      <h3 className="mb-1.5 text-sm font-semibold">Lessons</h3>
      <ol className="space-y-1 text-sm">
        {lessons.map((l, i) => {
          const live = l.activity_id ? activities.find((a) => a.id === l.activity_id) : null
          return (
            <li key={l.id} className="flex items-baseline gap-2">
              <span className="w-4 shrink-0 text-right text-xs text-muted-foreground">{i + 1}.</span>
              {live ? (
                <Link to={`/activities/${live.id}`} className="hover:underline">
                  {live.title}
                </Link>
              ) : (
                <span className="text-muted-foreground">
                  {l.title}
                  <Badge variant="outline" className="ml-2 text-[10px]">
                    topic deleted
                  </Badge>
                </span>
              )}
            </li>
          )
        })}
      </ol>
    </div>
  )
}

/**
 * Pick the topics taught in this workshop, in the order they'll be taught.
 *
 * Two lists rather than one checkbox column: the selected half is ordered and
 * the pool is not, and a single list can't show both. Order is the selection
 * order, moved with the arrows — a workshop's running order rarely matches the
 * curriculum's own display_order, which is what a checkbox list would force.
 */
function LessonPicker({
  activities,
  selected,
  onChange,
}: {
  activities: Activity[]
  selected: string[]
  onChange: (next: string[]) => void
}) {
  const byId = React.useMemo(() => new Map(activities.map((a) => [a.id, a])), [activities])
  const pool = activities.filter((a) => !selected.includes(a.id))

  function move(i: number, delta: number) {
    const j = i + delta
    if (j < 0 || j >= selected.length) return
    const next = [...selected]
    ;[next[i], next[j]] = [next[j], next[i]]
    onChange(next)
  }

  return (
    <div className="space-y-2">
      <Label>Lessons</Label>
      <p className="text-xs text-muted-foreground">
        The topics covered in this session, in running order. Optional — a working session
        doesn’t have to teach anything.
      </p>

      {selected.length > 0 && (
        <ol className="space-y-1 rounded-md border p-2">
          {selected.map((id, i) => (
            <li key={id} className="flex items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-muted/60">
              <span className="w-4 shrink-0 text-right text-xs text-muted-foreground">{i + 1}.</span>
              <span className="min-w-0 flex-1 truncate">{byId.get(id)?.title ?? 'Unknown topic'}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                disabled={i === 0}
                onClick={() => move(i, -1)}
                aria-label="Move up"
              >
                <ChevronUp className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                disabled={i === selected.length - 1}
                onClick={() => move(i, 1)}
                aria-label="Move down"
              >
                <ChevronDown className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => onChange(selected.filter((s) => s !== id))}
                aria-label="Remove lesson"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </li>
          ))}
        </ol>
      )}

      {activities.length === 0 ? (
        <p className="rounded-md border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">
          This team has no topics yet.
        </p>
      ) : pool.length === 0 ? (
        <p className="text-xs text-muted-foreground">Every topic is in this workshop.</p>
      ) : (
        <div className="max-h-40 space-y-0.5 overflow-y-auto rounded-md border p-1">
          {pool.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => onChange([...selected, a.id])}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
            >
              <Plus className="h-3 w-3 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate">{a.title}</span>
              <Badge variant="muted" className="shrink-0 text-[10px]">
                {a.kind}
              </Badge>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function StatusBadge({ status }: { status: WorkshopStatus }) {
  const variant =
    status === 'complete' ? 'success' : status === 'cancelled' ? 'destructive' : status === 'draft' ? 'muted' : 'outline'
  return (
    <Badge variant={variant} className="text-[10px]">
      {status}
    </Badge>
  )
}

/**
 * Bring `workshop_topics` in line with what the picker holds.
 *
 * A diff rather than delete-all-and-reinsert, for one reason that matters:
 * rows whose `activity_id` is null are the record of a lesson whose topic was
 * later deleted (0033), the picker can't represent them, and a wipe would
 * destroy exactly the history the table was shaped to keep. They're left alone
 * and sort after the linked ones.
 *
 * `title` is left out of the insert on purpose — the trigger fills it from the
 * topic, so the snapshot is the server's own read rather than whatever the
 * client believed the title was.
 */
async function syncLessons(
  workshopId: string,
  teamId: string,
  existing: WorkshopTopic[],
  selected: string[],
) {
  const linked = existing.filter((l) => l.activity_id)

  const removed = linked.filter((l) => !selected.includes(l.activity_id as string))
  if (removed.length > 0) {
    const { error } = await sb()
      .from('workshop_topics')
      .delete()
      .in('id', removed.map((l) => l.id))
    if (error) throw error
  }

  const added = selected.filter((id) => !linked.some((l) => l.activity_id === id))
  if (added.length > 0) {
    const { error } = await sb()
      .from('workshop_topics')
      .insert(
        added.map((activity_id) => ({
          workshop_id: workshopId,
          // Re-derived from the workshop by the trigger, so this is a formality.
          team_id: teamId,
          activity_id,
          display_order: selected.indexOf(activity_id),
        })),
      )
    if (error) throw error
  }

  // Reorder whatever was already there and stayed. Only the rows whose position
  // actually moved, so a save that didn't touch the order writes nothing.
  const moved = linked.filter(
    (l) =>
      selected.includes(l.activity_id as string) &&
      l.display_order !== selected.indexOf(l.activity_id as string),
  )
  for (const l of moved) {
    const { error } = await sb()
      .from('workshop_topics')
      .update({ display_order: selected.indexOf(l.activity_id as string) })
      .eq('id', l.id)
    if (error) throw error
  }
}

function WorkshopEditor({
  teamId,
  workshop,
  activities,
  lessons,
  isCoach,
  internalNote,
  onClose,
  onSaved,
}: {
  teamId: string
  workshop?: Workshop
  activities: Activity[]
  /** This workshop's existing lesson rows, already in display order. */
  lessons: WorkshopTopic[]
  /** Whether to offer the internal notes pane at all. The database decides who
   *  may actually write one; this only decides whether to draw the field. */
  isCoach: boolean
  /** DPL's existing note for this workshop, if there is one and you can see it. */
  internalNote?: WorkshopInternalNote
  onClose: () => void
  onSaved: () => void
}) {
  const [title, setTitle] = React.useState(workshop?.title ?? '')
  const [description, setDescription] = React.useState(workshop?.description ?? '')
  const [facilitator, setFacilitator] = React.useState(workshop?.facilitator ?? '')
  const [location, setLocation] = React.useState(workshop?.location ?? '')
  const [meetingUrl, setMeetingUrl] = React.useState(workshop?.meeting_url ?? '')
  const [startsAt, setStartsAt] = React.useState(toLocalInput(workshop?.starts_at))
  const [endsAt, setEndsAt] = React.useState(toLocalInput(workshop?.ends_at))
  const [status, setStatus] = React.useState<WorkshopStatus>(workshop?.status ?? 'scheduled')
  const [agenda, setAgenda] = React.useState(workshop?.agenda ?? '')
  const [notes, setNotes] = React.useState(workshop?.notes ?? '')
  const [outcomes, setOutcomes] = React.useState(workshop?.outcomes ?? '')
  const [internal, setInternal] = React.useState(internalNote?.notes ?? '')
  // Only the still-linked lessons are editable here. A row whose topic was
  // deleted has nothing to select, so it is left untouched by the sync below
  // rather than being offered and silently dropped.
  const [topicIds, setTopicIds] = React.useState<string[]>(() =>
    lessons.flatMap((l) => (l.activity_id ? [l.activity_id] : [])),
  )
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const dirty = useDirty({
    title, description, facilitator, location, meetingUrl,
    startsAt, endsAt, status, agenda, notes, outcomes, internal, topicIds,
  })
  const guard = useDiscardGuard(dirty, onClose)

  async function save(e: React.FormEvent) {
    e.preventDefault()
    if (!title.trim()) return setError('Give the workshop a title.')
    if (startsAt && endsAt && new Date(endsAt) < new Date(startsAt)) {
      return setError('The end time is before the start time.')
    }
    let url = meetingUrl.trim()
    if (url && !/^https?:\/\//i.test(url)) url = `https://${url}`

    setBusy(true)
    setError(null)
    try {
      const row = {
        team_id: teamId,
        title: title.trim(),
        description: description.trim() || null,
        facilitator: facilitator.trim() || null,
        location: location.trim() || null,
        meeting_url: url || null,
        starts_at: startsAt ? new Date(startsAt).toISOString() : null,
        ends_at: endsAt ? new Date(endsAt).toISOString() : null,
        status,
        agenda: agenda.trim() || null,
        notes: notes.trim() || null,
        outcomes: outcomes.trim() || null,
      }
      let workshopId = workshop?.id
      if (workshop) {
        const { error: err } = await sb().from('workshops').update(row).eq('id', workshop.id)
        if (err) throw err
      } else {
        const { data: created, error: err } = await sb()
          .from('workshops')
          .insert(row)
          .select('id')
          .single()
        if (err) throw err
        workshopId = (created as { id: string }).id
      }

      if (workshopId) await syncLessons(workshopId, teamId, lessons, topicIds)

      // The internal note is its own row with its own audience, so it is its own
      // write — and only attempted when there is something to say or something
      // to clear. A member never reaches this: the field isn't drawn for them,
      // and the policy would refuse it if it were.
      if (isCoach && workshopId && (internal.trim() || internalNote)) {
        const body = internal.trim()
        const { error: err } = body
          ? await sb()
              .from('workshop_internal_notes')
              .upsert(
                { workshop_id: workshopId, team_id: teamId, notes: body },
                { onConflict: 'workshop_id' },
              )
          : // Emptied on purpose: drop the row rather than keep an empty one, so
            // "is there an internal note" stays one test everywhere.
            await sb().from('workshop_internal_notes').delete().eq('workshop_id', workshopId)
        if (err) throw err
      }

      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && guard.requestClose()}>
      {/* Full screen rather than a panel: this form is now where a meeting's
          notes get written during or straight after the session, and writing
          prose in a 2xl box with the page showing round the edges is what made
          people write it somewhere else instead. */}
      <DialogFullScreenContent>
        <DiscardPrompt guard={guard} what="this meeting" />
        <form onSubmit={save} className="flex h-full flex-col">
          {/* Pinned header. The actions live up here because the form is taller
              than the window with notes in it, and a Save button you have to
              scroll to is a Save button people stop trusting. */}
          <div className="flex shrink-0 items-center gap-3 border-b px-4 py-3 sm:px-6">
            <div className="min-w-0">
              <DialogTitle className="truncate text-base">
                {workshop ? workshop.title || 'Edit meeting' : 'New meeting'}
              </DialogTitle>
              <DialogDescription className="truncate text-xs">
                Agenda, notes and outcomes accept Markdown.
              </DialogDescription>
            </div>
            <div className="ml-auto flex shrink-0 items-center gap-2">
              <Button type="button" variant="ghost" onClick={guard.requestClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy}>
                {workshop ? 'Save changes' : 'Create meeting'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={onClose}
                aria-label="Close"
              >
                <X />
              </Button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
            {/* Two columns on a wide screen: the details are a form, the notes
                are writing, and they want different amounts of room. One column
                below lg, in the order you fill them. */}
            <div className="mx-auto grid max-w-6xl gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="wtitle">Title</Label>
                  <Input id="wtitle" autoFocus value={title} onChange={(e) => setTitle(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wdesc">Description</Label>
                  <Textarea id="wdesc" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="wstart">Starts</Label>
                    <Input id="wstart" type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="wend">Ends</Label>
                    <Input id="wend" type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="wfac">Facilitator</Label>
                    <Input id="wfac" value={facilitator} onChange={(e) => setFacilitator(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="wloc">Location</Label>
                    <Input id="wloc" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Room / city" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="wstatus">Status</Label>
                    <Select value={status} onValueChange={(v) => setStatus(v as WorkshopStatus)}>
                      <SelectTrigger id="wstatus">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(['draft', 'scheduled', 'complete', 'cancelled'] as WorkshopStatus[]).map((s) => (
                          <SelectItem key={s} value={s}>
                            {s[0].toUpperCase() + s.slice(1)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="wurl">Meeting link</Label>
                  <Input id="wurl" value={meetingUrl} onChange={(e) => setMeetingUrl(e.target.value)} placeholder="https://…" />
                </div>

                <Separator />

                <LessonPicker activities={activities} selected={topicIds} onChange={setTopicIds} />
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="wagenda">Agenda</Label>
                  <Textarea
                    id="wagenda"
                    rows={5}
                    value={agenda}
                    onChange={(e) => setAgenda(e.target.value)}
                    className="font-mono text-xs"
                    placeholder={'1. Framing (15 min)\n2. Diverge (30 min)\n3. Converge (30 min)'}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="wnotes">Meeting notes</Label>
                  <Textarea
                    id="wnotes"
                    rows={10}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="font-mono text-xs"
                    placeholder="What was said, what was decided, who is doing what"
                  />
                  <p className="text-xs text-muted-foreground">Everyone on the team can read these.</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="woutcomes">Outcomes</Label>
                  <Textarea
                    id="woutcomes"
                    rows={3}
                    value={outcomes}
                    onChange={(e) => setOutcomes(e.target.value)}
                    className="font-mono text-xs"
                    placeholder="Filled in afterwards — decisions made, next steps"
                  />
                </div>

                {isCoach && (
                  <div className="space-y-2 rounded-md border border-dashed border-warning/50 bg-warning/5 p-3">
                    <Label htmlFor="winternal" className="flex items-center gap-2">
                      Internal notes
                      <Badge variant="outline" className="border-warning/50 text-[10px] font-medium uppercase tracking-wide text-warning">
                        DPL only
                      </Badge>
                    </Label>
                    <Textarea
                      id="winternal"
                      rows={8}
                      value={internal}
                      onChange={(e) => setInternal(e.target.value)}
                      className="font-mono text-xs"
                      placeholder="How it actually went. What to raise with the lead. What to do differently."
                    />
                    <p className="text-xs text-muted-foreground">
                      Visible to DPL coaches and admins only — not to the team, and not to the team lead.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {error && <p className="mx-auto mt-4 max-w-6xl text-sm text-destructive">{error}</p>}
          </div>
        </form>
      </DialogFullScreenContent>
    </Dialog>
  )
}

export { formatDate }
